import { useCallback } from "react";
import { removeUserFromInvitedUsers, removeUserFromRoom } from "../../firebase";
import ParticipantsPanel from "../ParticipantsPanel";
import { Check, Clock, X as CloseIcon, XCircle } from "lucide-react";
import { AccessRoomResponseOptions, RoomAccessRequest } from "./types";
import { getFunctions, httpsCallable } from "firebase/functions";

type ParticipantsPanelModalProps = {
    isPanelOpen?: boolean;
    meetingClient: any;
    roomId: string | null;
    user: any;
    sessionSetting: {
        isRoomModerator: boolean;
    } | null;
    setIsPanelOpen: (open: boolean) => void;
    accessRequests?: RoomAccessRequest[];
    onRespondToAccessRequest?: (requestId: string, action: AccessRoomResponseOptions.APPROVE | AccessRoomResponseOptions.DENY) => Promise<void> | void;
    processingRequestId?: string | null;
}

const ParticipantsPanelModal: React.FC<ParticipantsPanelModalProps> = ({ meetingClient, roomId, user, sessionSetting, isPanelOpen, setIsPanelOpen, accessRequests, onRespondToAccessRequest, processingRequestId }) => {

    const functions = getFunctions();
    const cancelRoomAccessRequestFunction = httpsCallable<{ roomId: string, userId: string }, { success: boolean; message: string }>(
        functions,
        "cancelRoomAccessRequest",
    );

    const handleKickUser = useCallback(
        async (userIdToKick: string) => {
            // Apenas o dono da sala pode expulsar participantes
            if (
                !meetingClient ||
                !sessionSetting?.isRoomModerator ||
                userIdToKick === user.uid ||
                !roomId
            ) {
                return;
            }

            try {
                await Promise.all([
                    removeUserFromRoom(roomId, userIdToKick),
                    removeUserFromInvitedUsers(roomId, userIdToKick),
                    cancelRoomAccessRequestFunction({ roomId, userId: userIdToKick }),
                ]);

                const participants = meetingClient.participants.joined.toArray();
                const peer = participants.find(
                    (p: any) => p.customParticipantId === userIdToKick,
                );

                if (peer?.kick) {
                    await peer.kick();
                }
            } catch (error) {
                console.error("Kick failed:", error);
                throw error;
            }
        },
        [meetingClient, roomId, user, sessionSetting],
    );

    const handleDisableAudio = useCallback(
        async (userId: string) => {
            // Apenas o dono da sala pode desabilitar áudio de participantes
            if (
                !meetingClient ||
                !sessionSetting?.isRoomModerator
            ) {
                return;
            }

            try {
                const participants = meetingClient.participants.joined.toArray();
                const peer = participants.find(
                    (p: any) => p.customParticipantId === userId,
                );

                if (peer && peer.audioEnabled) {
                    await (peer as any).disableAudio();
                }
            } catch (error) {
                console.error("Disable audio failed:", error);
                throw error;
            }
        },
        [meetingClient, sessionSetting],
    );

    const handleDisableVideo = useCallback(
        async (userId: string) => {
            // Apenas o dono da sala pode desabilitar vídeo de participantes
            if (
                !meetingClient ||
                !sessionSetting?.isRoomModerator
            ) {
                return;
            }

            try {
                const participants = meetingClient.participants.joined.toArray();
                const peer = participants.find(
                    (p: any) => p.customParticipantId === userId,
                );

                if (peer && peer.videoEnabled) {
                    await (peer as any).disableVideo();
                }
            } catch (error) {
                console.error("Disable video failed:", error);
                throw error;
            }
        },
        [meetingClient, sessionSetting],
    );

    if (!isPanelOpen || !roomId) {
        return null;
    }

    return (
            <div
                className="fixed inset-0 bg-black/60 z-[100] flex justify-center items-center p-4"
                onClick={() => setIsPanelOpen(false)}
            >
                <div
                    className="relative bg-white rounded-lg w-full max-w-sm h-auto max-h-[80vh] overflow-hidden flex flex-col"
                    onClick={(e) => e.stopPropagation()}
                >
                    {sessionSetting?.isRoomModerator && (
                        <div className="border-b border-gray-200 p-4">
                            <div className="flex items-center justify-between">
                                <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                                    <Clock className="h-4 w-4 text-blue-500" />
                                    Solicitações de acesso
                                    {accessRequests && accessRequests.length > 0 && (
                                        <span className="inline-flex items-center justify-center text-xs font-semibold text-white bg-blue-600 px-2 py-0.5 rounded-full">
                                            {accessRequests.length}
                                        </span>
                                    )}
                                </h3>
                            </div>
                            {accessRequests && accessRequests.length > 0 ? (
                                <ul className="mt-3 space-y-3">
                                    {accessRequests.map((request) => {
                                        const isProcessing = processingRequestId === request.id;
                                        const displayName = request.name || request.email || request.userId;
                                        const formattedTime = request.requestedAt
                                            ? request.requestedAt.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })
                                            : null;

                                        return (
                                            <li key={request.id} className="bg-gray-50 rounded-lg p-3 space-y-3">
                                                <div className="flex items-center justify-between gap-3">
                                                    <div>
                                                        <p className="text-sm font-medium text-gray-800">{displayName}</p>
                                                        {request.email && request.email !== displayName && (
                                                            <p className="text-xs text-gray-500">{request.email}</p>
                                                        )}
                                                    </div>
                                                    {formattedTime && (
                                                        <span className="text-[11px] text-gray-400">{formattedTime}</span>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        type="button"
                                                        disabled={isProcessing}
                                                        onClick={() => onRespondToAccessRequest?.(request.id, AccessRoomResponseOptions.APPROVE)}
                                                        className="inline-flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-full bg-green-500 text-white hover:bg-green-600 transition-colors disabled:bg-green-300 disabled:cursor-not-allowed"
                                                    >
                                                        {isProcessing ? (
                                                            <span className="animate-spin rounded-full h-3 w-3 border-b-2 border-white" />
                                                        ) : (
                                                            <Check className="h-3.5 w-3.5" />
                                                        )}
                                                        Aprovar
                                                    </button>
                                                    <button
                                                        type="button"
                                                        disabled={isProcessing}
                                                        onClick={() => onRespondToAccessRequest?.(request.id, AccessRoomResponseOptions.DENY)}
                                                        className="inline-flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-full bg-red-500 text-white hover:bg-red-600 transition-colors disabled:bg-red-300 disabled:cursor-not-allowed"
                                                    >
                                                        {isProcessing ? (
                                                            <span className="animate-spin rounded-full h-3 w-3 border-b-2 border-white" />
                                                        ) : (
                                                            <XCircle className="h-3.5 w-3.5" />
                                                        )}
                                                        Recusar
                                                    </button>
                                                </div>
                                            </li>
                                        );
                                    })}
                                </ul>
                            ) : (
                                <p className="mt-3 text-xs text-gray-500">Nenhum pedido pendente no momento.</p>
                            )}
                        </div>
                    )}

                    <div className="flex-1 overflow-y-auto">
                        <ParticipantsPanel
                            roomId={roomId}
                            onKickUser={handleKickUser}
                            onDisableAudio={handleDisableAudio}
                            onDisableVideo={handleDisableVideo}
                            meetingClient={meetingClient}
                        />
                    </div>
                    <button
                        onClick={() => setIsPanelOpen(false)}
                        className="absolute top-2 right-2 bg-gray-200 p-1 rounded-full hover:bg-gray-300"
                    >
                        <CloseIcon className="h-5 w-5 text-gray-600" />
                    </button>
                </div>
            </div>
    );
}

export default ParticipantsPanelModal;